import { useRef, useState, useCallback, useEffect } from "react";
import DataGrid from '../../../components/common/dataGrid/dataGrid.js';
import ButtonWithSearch from '../../../components/common/buttonWithSearch.js';
import LinearColoredComponent from '../../../components/common/linearColoredComponent.js';
import { SORT_ORDER } from '../../../constants/sortOrder';
import { PermissionsTableColumns } from "../../../utils/serviceData.js";

/**
 * PermissionSection Component
 * Displays a list of permissions with search, pagination, sorting, and edit functionality
 * Manages its own navigator state for pagination, sorting, and search
 * 
 * @param {Object} permissionsData - Permission data object containing:
 *   - columns: Array of column definitions
 *   - values: Array of permission data
 *   - loading: Boolean for loading state
 *   - initLoading: Boolean for initial loading state
 *   - assignNum: Object for assigned numbers calculation
 *   - fetchPermissionsTable: Function to fetch permissions with navigator state
 * @param {Function} onEditPermission - Handler for edit permission action
 * @param {String} campaignValue - Current campaign value to trigger refresh
 */
export default function PermissionSection({
  permissionsData,
  onEditPermission,
  campaignValue,
}) {
  const menuLeft = useRef(null);
  const fetchPermissionsRef = useRef(null);
  const isInitialMount = useRef(true);
  const fetchingRef = useRef(false);
  
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "permission_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: {},
  });

  // Store fetchPermissions in ref to avoid dependency issues
  useEffect(() => {
    fetchPermissionsRef.current = permissionsData?.fetchPermissionsTable;
  }, [permissionsData?.fetchPermissionsTable]);

  // Initialize and refresh when campaign changes - only when campaign actually changes
  useEffect(() => {
    // Skip on initial mount if no campaign value
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!campaignValue || !fetchPermissionsRef.current) return;
    }

    // Only proceed if campaign value actually changed
    if (fetchPermissionsRef.current) {
      // Prevent concurrent calls
      if (fetchingRef.current) return;

      const initialNavigator = {
        page: 1,
        first: 0,
        rows: 10,
        totalRecords: 1,
        sortBy: "permission_name",
        sortDir: SORT_ORDER.ASC,
        searchCriteria: {},
      };
      setNavigator(initialNavigator);
      fetchingRef.current = true;
      fetchPermissionsRef.current(initialNavigator, setNavigator).finally(() => {
        fetchingRef.current = false;
      });
    }
    // Only depend on campaignValue - fetchPermissions is stored in ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page change
  const handlePageChange = useCallback((event) => {
    if (!fetchPermissionsRef.current || fetchingRef.current) return;
    
    setNavigator((prev) => {
      const newNavigator = {
        ...prev,
        first: event?.first || 0,
        page: (event?.page || 0) + 1,
      };
      fetchingRef.current = true;
      fetchPermissionsRef.current(newNavigator, setNavigator).finally(() => {
        fetchingRef.current = false;
      });
      return newNavigator;
    });
  }, []);

  // Handle row count change
  const handleRowCountChange = useCallback((e) => {
    if (!fetchPermissionsRef.current || fetchingRef.current) return;
    
    setNavigator((prev) => {
      const newNavigator = {
        ...prev,
        rows: parseInt(e?.target?.value) || 10,
        page: 1,
        first: 0,
      };
      fetchingRef.current = true;
      fetchPermissionsRef.current(newNavigator, setNavigator).finally(() => {
        fetchingRef.current = false;
      });
      return newNavigator;
    });
  }, []);

  // Handle sort
  const handleSort = useCallback((event) => {
    if (!fetchPermissionsRef.current || fetchingRef.current) return;
    
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => {
      const newNavigator = {
        ...prev,
        sortBy: event?.sortField,
        sortDir: sortOrder,
        sortFields: [[event?.sortField, sortOrder]],
      };
      fetchingRef.current = true;
      fetchPermissionsRef.current(newNavigator, setNavigator).finally(() => {
        fetchingRef.current = false;
      });
      return newNavigator;
    });
  }, []);

  // Handle search
  const handleSearchChange = useCallback((value) => {
    if (!fetchPermissionsRef.current || fetchingRef.current) return;
    
    setNavigator((prev) => {
      const newNavigator = {
        ...prev,
        searchCriteria: { searchvalue: value },
      };
      fetchingRef.current = true;
      fetchPermissionsRef.current(newNavigator, setNavigator).finally(() => {
        fetchingRef.current = false;
      });
      return newNavigator;
    });
  }, []);

  return (
    <DataGrid
      header={
        <ButtonWithSearch
          isSearchEnabled={true}
          searchPlaceHolder={"Search Permissions"}
          searchHeaderLable={"Permissions"}
          isCalenderEnabled={false}
          isButtonEnabledLeft={false}
          isButtonEnabledRight={false}
          loading={permissionsData?.initLoading}
          styles={{ gridTemplateColumns: "1fr 400px" }}
          onChange={handleSearchChange}
        />
      }
      onSortClick={handleSort}
      onPageChange={handlePageChange}
      onRowCountChange={handleRowCountChange}
      sortable={true}
      isPaginatorEnabled={true}
      navigator={navigator}
      column={
        permissionsData?.columns && permissionsData.columns.length > 0
          ? permissionsData.columns
          : PermissionsTableColumns
      }
      value={permissionsData?.values || []}
      ref={menuLeft}
      loading={permissionsData?.loading}
      customColumn={["role_names"]}
      columnCallBack={(data, title) => (
        <LinearColoredComponent
          data={data}
          title={title}
          assignNum={permissionsData?.assignNum}
        />
      )}
      isShowEdit={true}
      onEdit={onEditPermission}
    />
  );
}

